import { Box, useMediaQuery, useTheme } from "@mui/material";

import { useAppSelector } from "app/redux-store-interactions";
import { OfflineIndicator } from "components/offlineIndicator";
import { MINIMAP_LAYOUT, MobileToolbars, PROCORE_TOP_BAR, TOOLBAR_LAYOUT } from "components/toolbars";
import { UndoRedoStackVisualizer } from "components/undoRedoStackVisualizer";
import { DrawerContainer, SidePanelContainer, useDrawerRegistration, usePanelRegistration } from "components/window";
import { useIsPhone, useIsPhonePortrait, usePanelMode } from "contexts/panelMode";
import { VISIBILITY_TOOLBAR_IDS } from "contexts/toolbars";
import { AxisSphere } from "features/axisSphere/AxisSphere";
import { LargeBookmarkWarningDialog } from "features/bookmarks/largeBookmarkWarningDialog";
import { DetailsMenu } from "features/detailsMenu/detailsMenu";
import { GlobalSearch } from "features/globalSearch/globalSearch";
import { Joysticks } from "features/joysticks/joysticks";
import LocationHud from "features/locationHud/locationHud";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";
import { PinOffsetCursor } from "features/markers/PinOffsetCursor";
import { MiniMapMobile, MiniMapWeb } from "features/miniMap";
import { MeasurementOverlayButtons, MobileMeasureCursor, MobileMeasureIntegration } from "features/mobileMeasure";
import { NavigationCube } from "features/navigationCube";
import { IncrementalLoadingPopup } from "features/offline/incrementalLoadingPopup";
import { PickerEscSnackbar } from "features/pickerEscSnackbar/pickerEscSnackbar";
import { PrimaryMenu, PrimaryMenuNew } from "features/primaryMenu";
import { NavWheelPrimaryMenu } from "features/primaryMenu/navWheelPrimaryMenu";
import { QuickAccessMenu as ProcoreQuickAccessMenu } from "features/quickAccessMenu/procore/quickAccessMenu";
import { QuickAccessMenu } from "features/quickAccessMenu/quickAccessMenu";
import { QuirkAlert } from "features/quirkAlert";
import { ReleaseCandidateToggler } from "features/releaseCandidateToggler";
import { selectNavigationCube } from "features/render";
import { SelectionModifierMenu } from "features/selectionModifierMenu";
import { WidgetGroupPanel } from "features/widgetGroupPanel/widgetGroupPanel";
import { Widgets } from "features/widgets";
import { useHandleWidgetLayout } from "features/widgets/useHandleWidgetLayout";
import {
    selectIsAxisSphereVisible,
    selectIsJoysticksVisible,
    selectIsMiniMapVisible,
    selectIsProcoreDesign,
    selectIsProcoreMobileDesign,
    selectNewDesign,
    selectWidgetLayout,
} from "slices/explorer";
import { selectIsToolbarVisible } from "slices/toolbarVisibility";

import { CameraIsTooFarMessage } from "./cameraIsTooFarMessage";
import { CoachmarkDisplay } from "./coachmarkDisplay";
import GlobalSnackbar from "./globalSnackbar";
import NewProjectVersionSnackbar from "./newProjectVersionSnackbar";
import { UnusuallyFarObjectsMessage } from "./unusuallyFarObjectsMessage";
import WeakDeviceSnackbar from "./weakDeviceSnackbar";

export function Hud() {
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);

    if (isProcoreDesign) {
        if (isProcoreMobileDesign) {
            return <HudProcoreMobile />;
        } else {
            return <HudProcore />;
        }
    }

    if (newDesign) {
        return (
            <>
                <HudNew />
                <IncrementalLoadingPopup />
                <QuirkAlert />
            </>
        );
    }

    return (
        <>
            <HudOld />
            <IncrementalLoadingPopup />
            <QuirkAlert />
        </>
    );
}

function HudNew() {
    const navigationCube = useAppSelector(selectNavigationCube);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

    useHandleWidgetLayout();

    return (
        <>
            <LargeBookmarkWarningDialog />
            {navigationCube.enabled && <NavigationCube />}
            <Box
                sx={{
                    position: "absolute",
                    inset: theme.spacing(
                        isSmall ? theme.customSpacing.hudPaddingMobile : theme.customSpacing.hudPadding,
                    ),
                    pointerEvents: "none",
                }}
            >
                <LocationHud />
                <QuickAccessMenu />
                {!isSmall && <WidgetGroupPanel />}
                <Widgets />
                <GlobalSnackbar />
                <CameraIsTooFarMessage />
                <UnusuallyFarObjectsMessage />
                <PickerEscSnackbar />
                <WeakDeviceSnackbar />
                <NewProjectVersionSnackbar />
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    inset: isSmall ? 0 : theme.spacing(theme.customSpacing.hudPadding),
                    pointerEvents: "none",
                }}
            >
                <PrimaryMenuNew />
            </Box>
            <GlobalSearch />
        </>
    );
}

/**
 * Panel container that renders SidePanel or Drawer based on screen size.
 * Phones (short dimension ≤ 430px): Drawers only.
 * Tablets/Desktop: SidePanels only.
 */
function AdaptivePanelContainer() {
    const panelMode = usePanelMode();

    if (panelMode === "sidePanel") {
        return <SidePanelContainer />;
    } else {
        return <DrawerContainer />;
    }
}

function HudProcore() {
    // Register side panels and drawers on mount
    usePanelRegistration();
    useDrawerRegistration();
    const quickAccessVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.QUICK_ACCESS));
    const detailsVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.DETAILS));
    const navWheelVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.NAV_WHEEL));
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible);

    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                }}
            >
                {quickAccessVisible && <ProcoreQuickAccessMenu />}
                {detailsVisible && <DetailsMenu />}
                {navWheelVisible && <NavWheelPrimaryMenu />}
                {!quickAccessVisible && isAxisSphereVisible && (
                    <Box sx={{ position: "absolute", right: "16px", top: "16px" }}>
                        <AxisSphere />
                    </Box>
                )}
                <Box sx={{ display: isMiniMapVisible ? "contents" : "none" }}>
                    <MiniMapWeb />
                </Box>
                <CoachmarkDisplay />
                <PinOffsetCursor />
                <GlobalSnackbar />
                <ReleaseCandidateToggler />
                <UndoRedoStackVisualizer />
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        pointerEvents: "none",
                        zIndex: 1200,
                    }}
                >
                    <OfflineIndicator />
                </Box>
            </Box>
        </>
    );
}

function HudProcoreMobile() {
    // Register side panels and drawers on mount
    usePanelRegistration();
    useDrawerRegistration();
    const isPhone = useIsPhone();
    const isPhonePortrait = useIsPhonePortrait();
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isJoysticksVisible = useAppSelector(selectIsJoysticksVisible);
    const topBarHeight = isPhonePortrait ? PROCORE_TOP_BAR.HEIGHT_PORTRAIT : PROCORE_TOP_BAR.HEIGHT;

    return (
        <>
            <Box
                id="mobile-hud-container"
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Core mobile components */}
                <MobileToolbars.TopBar />

                <Box
                    sx={{
                        position: "absolute",
                        top: `env(safe-area-inset-top, 0px)`,
                        left: "50%",
                        transform: "translateX(-50%)",
                        pointerEvents: "none",
                        zIndex: 1200,
                    }}
                >
                    <OfflineIndicator />
                </Box>

                {isAxisSphereVisible && (
                    <Box
                        sx={{
                            position: "absolute",
                            right: `${isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET}px`,
                            top: `${topBarHeight + (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px`,
                            zIndex: MINIMAP_LAYOUT.Z_INDEX,
                        }}
                    >
                        <AxisSphere size={isPhone ? 100 : 130} />
                    </Box>
                )}

                {/* MiniMap - right below top bar, portrait only */}

                <Box
                    sx={{
                        mt: `${topBarHeight + (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px`,
                        ml: `max(${isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET}px, env(safe-area-inset-left, 0px))`,
                        mr: `max(${isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET}px, env(safe-area-inset-right, 0px))`,
                        zIndex: MINIMAP_LAYOUT.Z_INDEX,
                    }}
                >
                    <MiniMapMobile />
                </Box>

                {/* Spacer to push joysticks to bottom */}
                <Box flex={1} sx={{ pointerEvents: "none" }} />

                {/* Joysticks - just above bottom toolbars */}
                {isJoysticksVisible && <Joysticks />}

                <CoachmarkDisplay
                    topOffset={`${(isPhonePortrait ? PROCORE_TOP_BAR.HEIGHT_PORTRAIT : PROCORE_TOP_BAR.HEIGHT) + 12}px`}
                />
                <MobileToolbars.BottomToolbar />
                <MobileToolbars.UndoRedoToolbar />
                <Box
                    sx={{
                        visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                        pointerEvents: nonMarkerUIIsHidden ? "none" : undefined,
                    }}
                >
                    <AdaptivePanelContainer />
                </Box>

                {/* Mobile measurement cursor and integration */}
                <PinOffsetCursor />
                <MobileMeasureCursor />
                <MobileMeasureIntegration />
                <MeasurementOverlayButtons />

                {/* Essential overlays */}
                <GlobalSnackbar />
            </Box>
        </>
    );
}

function HudOld() {
    const navigationCube = useAppSelector(selectNavigationCube);

    useHandleWidgetLayout();
    const layout = useAppSelector(selectWidgetLayout);

    const getGridLayout = () => {
        if (layout.widgets === 4) {
            return {
                gridTemplateColumns: "1fr 1fr repeat(2, minmax(420px, 1fr))",
                gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            };
        } else if (layout.widgets === 2) {
            return {
                gridTemplateColumns: "1fr 1fr minmax(420px, 1fr)",
                gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            };
        } else if (layout.widgets === 1 && layout.sideBySide) {
            return {
                gridTemplateColumns: "1fr 1fr minmax(420px, 1fr)",
                gridTemplateRows: "repeat(2, 1fr)",
            };
        } else if (layout.widgets === 1 && !layout.sideBySide) {
            return {
                gridTemplateColumns: "1fr 1fr 1fr",
                gridTemplateRows: "repeat(2, minmax(0, 1fr))",
            };
        }
    };

    return (
        <>
            <LargeBookmarkWarningDialog />
            {navigationCube.enabled && <NavigationCube />}
            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                padding={2}
                display="grid"
                sx={{
                    pointerEvents: "none",
                    ...getGridLayout(),
                }}
            >
                <SelectionModifierMenu />
                <PrimaryMenu />
                <Widgets />
                <PickerEscSnackbar />
                <WeakDeviceSnackbar />
                <NewProjectVersionSnackbar />
            </Box>
        </>
    );
}
